/**
 * SCRUM-236 (DS-015 §13.8, F11) — the per-(entityType, field) value-formatter
 * registry.
 *
 * Each tracked field maps to { label message id, value kind, (enum) value-label
 * ids }. The registry is kept in lock-step with the backend classification via
 * the generated `@artemis/contracts` manifest (`ENTITY_CHANGE_FIELDS`): the
 * coverage drift test asserts the registry's field set equals the manifest's
 * tracked ∪ redacted set EXACTLY. A new backend tracked field therefore fails
 * the FE gate until a formatter + label are added — fail-closed, mirroring the
 * backend C1 completeness invariant (F11: drift = wrong/blank render).
 *
 * ── For maintainers ───────────────────────────────────────────────────────
 * When the FE coverage drift test goes red, the backend added/removed a tracked
 * field. To fix: add the field to PROPERTY_FIELD_KINDS (+ PROPERTY_ENUM_VALUES
 * if it is an enum), add its label to `messages/en.ts`, and add an adversarial
 * test for any NEW value kind in `__tests__/values.spec.ts`. Do NOT loosen the
 * coverage test — its exact-set equality is the contract.
 */
import { ENTITY_CHANGE_FIELDS } from '@artemis/contracts';
import { enumValueId, fieldLabelId } from '../../i18n/message-ids.js';
import type { ValueKind } from './values.js';

export interface FieldFormatterSpec {
  /** react-intl message id for the field's human label. */
  readonly labelId: string;
  /** How to decode + render this field's value. */
  readonly kind: ValueKind;
  /** For `enum` kind: value → label message id. Absent for non-enum kinds. */
  readonly enumLabelIds?: Readonly<Record<string, string>>;
}

export type EntityFieldRegistry = Readonly<Record<string, Readonly<Record<string, FieldFormatterSpec>>>>;

const PROPERTY_ENTITY = 'Property';
const GUEST_ENTITY = 'Guest';
const PAYMENT_TYPE_ENTITY = 'PaymentType';
const FOLIO_ENTITY = 'Folio';
const PAYMENT_SCHEDULE_TEMPLATE_ENTITY = 'PaymentScheduleTemplate';
const PAYMENT_ENTITY = 'Payment';
const RESERVATION_ENTITY = 'Reservation';

/** DB-field (wire `FieldChange.field`) → value kind for Property. */
const PROPERTY_FIELD_KINDS: Readonly<Record<string, ValueKind>> = {
  unitName: 'text',
  unitAlias: 'text',
  websiteNameOverride: 'text',
  unitCategory: 'enum',
  listingType: 'enum',
  statusBilling: 'enum',
  statusVisibility: 'enum',
  descriptionCanonical: 'text',
  clonedFromPropertyId: 'id',
  clonedFromOrganizationId: 'id',
  // SCRUM-308 — the parent-unit self-FK (tracked backend-side); rendered as an
  // id reference, mirroring the clonedFromPropertyId self-reference precedent.
  parentPropertyId: 'id',
  photoCount: 'count',
  archivedAt: 'instant',
  weekdayBasePriceCents: 'cents',
  weekendBasePriceCents: 'cents',
  weekdayFloorRateCents: 'cents',
  weekendFloorRateCents: 'cents',
  licenseNumber: 'text',
  vatNumber: 'text',
  houseRules: 'text',
  aboutThisSpace: 'text',
  additionalRules: 'text',
  localLaws: 'text',
  streetLine1: 'text',
  streetLine2: 'text',
  city: 'text',
  state: 'text',
  postalCode: 'text',
  country: 'text',
  latitude: 'coordinate',
  longitude: 'coordinate',
  timezone: 'text',
  maxSleeps: 'count',
  maxAdults: 'count',
  standardCapacity: 'count',
  allowsChildren: 'boolean',
  allowsPets: 'boolean',
  bedrooms: 'count',
  bathroomsFull: 'count',
  bathroomsHalf: 'count',
  bathroomsShower: 'count',
  bathroomsBathtub: 'count',
  bedConfigurations: 'json',
  checkInOverride: 'timeOfDay',
  checkOutOverride: 'timeOfDay',
};

/** Enum members per enum field (mirror @artemis/contracts unions). */
const PROPERTY_ENUM_VALUES: Readonly<Record<string, readonly string[]>> = {
  unitCategory: ['home', 'condominium', 'villa', 'cabin', 'townhome', 'apartment', 'other'],
  listingType: ['entire_unit', 'private_room', 'shared_room', 'other'],
  statusBilling: ['active', 'inactive'],
  statusVisibility: ['bookable', 'non_bookable'],
};

/** DB-field (wire `FieldChange.field`) → value kind for Guest (SCRUM-257). */
const GUEST_FIELD_KINDS: Readonly<Record<string, ValueKind>> = {
  givenName: 'text',
  familyName: 'text',
  displayName: 'text',
  primaryEmail: 'text',
  primaryPhone: 'text',
  addressLine1: 'text',
  addressLine2: 'text',
  addressCity: 'text',
  addressStateProvince: 'text',
  addressPostalCode: 'text',
  addressCountry: 'text',
  tags: 'json',
  notes: 'json',
  source: 'enum',
  status: 'enum',
  mergedIntoId: 'id',
  deletedAt: 'instant',
};

/** Enum members per Guest enum field (mirror the backend domain unions). */
const GUEST_ENUM_VALUES: Readonly<Record<string, readonly string[]>> = {
  // GuestStatus union — guest.entity.ts GUEST_STATUS_VALUES.
  status: ['Active', 'Archived', 'MergedAway'],
  // GuestSource closed enum — guest-source.ts GUEST_SOURCE_VALUES (v1: Manual only).
  source: ['Manual'],
};

/** DB-field (wire `FieldChange.field`) → value kind for PaymentType (SCRUM-290). */
const PAYMENT_TYPE_FIELD_KINDS: Readonly<Record<string, ValueKind>> = {
  paymentName: 'text',
  baseSystemType: 'enum',
  requiresRefund: 'boolean',
  processor: 'text',
  status: 'enum',
  systemKey: 'enum',
};

/** Enum members per PaymentType enum field (mirror the backend domain unions). */
const PAYMENT_TYPE_ENUM_VALUES: Readonly<Record<string, readonly string[]>> = {
  // BaseSystemType — the 4 custom-mapping targets (CC/ACH are gateway-only, not selectable).
  baseSystemType: ['Cash', 'Owner', 'Adjustment', 'Channel'],
  // PaymentTypeStatus.
  status: ['Active', 'Inactive'],
  // SystemPaymentTypeKey — the 6 blueprint system keys.
  systemKey: ['CC', 'ACH', 'Cash', 'Adjustment', 'Owner', 'Channel'],
};

/** DB-field (wire `FieldChange.field`) → value kind for Folio (SCRUM-291, DS-021 §5.2). */
const FOLIO_FIELD_KINDS: Readonly<Record<string, ValueKind>> = {
  reservationId: 'id',
  currency: 'text',
  collectionModel: 'enum',
  totalDueCents: 'cents',
  netCollectedCents: 'cents',
  status: 'enum',
  // CXL-1 (SCRUM-288 review): the staff funds-resolution decision is now tracked.
  cancellationPathway: 'enum',
};

/** Enum members per Folio enum field (mirror the backend domain unions). */
const FOLIO_ENUM_VALUES: Readonly<Record<string, readonly string[]>> = {
  // CollectionModel — money-of-record axis (booking BC).
  collectionModel: ['ChannelCollect', 'PropertyCollect'],
  // FolioStatus.
  status: ['Open', 'Closed', 'Locked'],
  // CancellationPathway (DS-021 §8) — the three funds-resolution routings.
  cancellationPathway: ['FutureStayCredit', 'Disburse', 'CancellationFee'],
};

/** DB-field (wire `FieldChange.field`) → value kind for PaymentScheduleTemplate (SCRUM-291, DS-021 §3.5). */
const PAYMENT_SCHEDULE_TEMPLATE_FIELD_KINDS: Readonly<Record<string, ValueKind>> = {
  scope: 'enum',
  propertyId: 'id',
  breakdownKind: 'enum',
  params: 'json',
  effectiveFrom: 'instant',
  status: 'enum',
};

/** Enum members per PaymentScheduleTemplate enum field (mirror the backend domain unions). */
const PAYMENT_SCHEDULE_TEMPLATE_ENUM_VALUES: Readonly<Record<string, readonly string[]>> = {
  // PaymentScheduleTemplateScope — scope discriminant (ADR 0026: no unit concept; Property IS the sellable unit).
  scope: ['org-global', 'property'],
  // BreakdownKind — discriminated installment params.
  breakdownKind: ['PaidInFull', 'TwoStep', 'MultiPayment'],
  // PaymentScheduleTemplateStatus.
  status: ['Active', 'Inactive'],
};

/** DB-field (wire `FieldChange.field`) → value kind for Payment (SCRUM-292, DS-021 §3.3). */
const PAYMENT_FIELD_KINDS: Readonly<Record<string, ValueKind>> = {
  state: 'enum',
  baseSystemType: 'enum',
  processingRail: 'enum',
  amountCents: 'cents',
  kind: 'enum',
  isCashMovement: 'boolean',
  requiresRefund: 'boolean',
  paymentTypeLabel: 'text',
  gatewayRef: 'text',
  processorName: 'text',
  occurredAt: 'instant',
};

/** Enum members per Payment enum field (mirror the backend domain unions). */
const PAYMENT_ENUM_VALUES: Readonly<Record<string, readonly string[]>> = {
  // PaymentState FSM states (DS-021 §4).
  state: ['Intended', 'Authorized', 'Captured', 'Settled', 'Voided', 'Failed', 'Refunded', 'PartiallyRefunded'],
  // BaseSystemType — all 6 values (CC/ACH are gateway-integrated but frozen in payment snapshots).
  baseSystemType: ['CC', 'ACH', 'Cash', 'Adjustment', 'Owner', 'Channel'],
  // ProcessingRail.
  processingRail: ['manual', 'gateway'],
  // PaymentKind.
  kind: ['charge', 'refund'],
};

/**
 * DB-field (wire `FieldChange.field`) → value kind for Reservation (SCRUM-338).
 *
 * The 18 tracked fields mirror the generated `ENTITY_CHANGE_FIELDS.Reservation`
 * manifest exactly (the coverage drift test enforces set-equality). The wire
 * values come straight from `ReservationPersistenceShape` via the booking
 * capture spec's tracked before/after image (bare enum strings / ids /
 * LocalDate `YYYY-MM-DD` strings / epoch-ms Instants), NOT wrapped values.
 *
 * Enum fields (closed wire unions → typed labels): `status`, `stayKind`,
 * `heardAbout`, `cancellationPathway` (the booking VO's own wire values, NOT the
 * Folio VO's — `'Hold-credit' | 'Disburse' | 'Cancellation-fee'`), `kind`
 * (`'stay' | 'block'`). `processor` is an OPEN channel string (e.g. `'airbnb'`)
 * with no closed catalog → rendered as `text` (honest raw render — acceptable
 * for a change log). `arrivalDate` / `departureDate` are LocalDate `YYYY-MM-DD`
 * wire strings rendered `localDate` (localized calendar date, SCRUM-338 A.2).
 */
const RESERVATION_FIELD_KINDS: Readonly<Record<string, ValueKind>> = {
  status: 'enum',
  stayKind: 'enum',
  heardAbout: 'enum',
  cancellationPathway: 'enum',
  kind: 'enum',
  reservationTypeKey: 'text',
  channelReservationId: 'text',
  guestDisplayName: 'text',
  arrivalDate: 'localDate',
  departureDate: 'localDate',
  // `processor` is an open channel identifier (no closed catalog) — honest raw text.
  processor: 'text',
  guestId: 'id',
  propertyId: 'id',
  guestCount: 'count',
  ratesLocked: 'boolean',
  // Instant fields serialize into the entity-change delta as ISO-8601 STRINGS
  // (e.g. `'2025-06-15T12:00:00.000Z'`), NOT epoch-ms numbers — unlike Property's
  // `archivedAt`. `formatInstant` fail-closes on a non-number; the `isoInstant`
  // ValueKind (SCRUM-338 A.2) parses the STRING and renders a localized UTC
  // date-time (fail-closed on an unparseable string → neutral).
  cancelledAt: 'isoInstant',
  confirmedAt: 'isoInstant',
  holdExpiresAt: 'isoInstant',
};

/** Enum members per Reservation enum field (mirror the booking domain unions). */
const RESERVATION_ENUM_VALUES: Readonly<Record<string, readonly string[]>> = {
  // ReservationStatus — reservation-status.vo.ts RESERVATION_STATUS_VALUES.
  status: ['QUO', 'HOLD', 'BKD', 'CI', 'CO', 'CXL', 'HIST'],
  // StayKind — stay-kind.vo.ts STAY_KIND_VALUES.
  stayKind: ['PayingGuest', 'OwnerStay', 'NonStayBlock'],
  // HeardAbout — heard-about.vo.ts HEARD_ABOUT_VALUES (§2.9 provenance).
  heardAbout: ['Social', 'Email', 'OTA', 'Direct', 'GEO', 'Search'],
  // CancellationPathway — cancellation-pathway.vo.ts CANCELLATION_PATHWAY_VALUES
  // (booking BC wire values; the Folio VO is a DIFFERENT catalog).
  cancellationPathway: ['Hold-credit', 'Disburse', 'Cancellation-fee'],
  // ReservationPersistenceShape.kind — the occupancy discriminant.
  kind: ['stay', 'block'],
};

function buildEnumLabelIds(
  entity: string,
  field: string,
  enumValues: Readonly<Record<string, readonly string[]>>,
): Record<string, string> {
  const ids: Record<string, string> = {};
  for (const value of enumValues[field] ?? []) {
    ids[value] = enumValueId(entity, field, value);
  }
  return ids;
}

function buildRegistry(
  entity: string,
  fieldKinds: Readonly<Record<string, ValueKind>>,
  enumValues: Readonly<Record<string, readonly string[]>>,
): Record<string, FieldFormatterSpec> {
  const specs: Record<string, FieldFormatterSpec> = {};
  for (const [field, kind] of Object.entries(fieldKinds)) {
    specs[field] =
      kind === 'enum'
        ? { labelId: fieldLabelId(entity, field), kind, enumLabelIds: buildEnumLabelIds(entity, field, enumValues) }
        : { labelId: fieldLabelId(entity, field), kind };
  }
  return specs;
}

/** The entity-agnostic registry. Add a new entity type as its own key. */
export const ENTITY_FIELD_REGISTRY: EntityFieldRegistry = {
  Folio: buildRegistry(FOLIO_ENTITY, FOLIO_FIELD_KINDS, FOLIO_ENUM_VALUES),
  Guest: buildRegistry(GUEST_ENTITY, GUEST_FIELD_KINDS, GUEST_ENUM_VALUES),
  Payment: buildRegistry(PAYMENT_ENTITY, PAYMENT_FIELD_KINDS, PAYMENT_ENUM_VALUES),
  PaymentScheduleTemplate: buildRegistry(
    PAYMENT_SCHEDULE_TEMPLATE_ENTITY,
    PAYMENT_SCHEDULE_TEMPLATE_FIELD_KINDS,
    PAYMENT_SCHEDULE_TEMPLATE_ENUM_VALUES,
  ),
  PaymentType: buildRegistry(PAYMENT_TYPE_ENTITY, PAYMENT_TYPE_FIELD_KINDS, PAYMENT_TYPE_ENUM_VALUES),
  Property: buildRegistry(PROPERTY_ENTITY, PROPERTY_FIELD_KINDS, PROPERTY_ENUM_VALUES),
  Reservation: buildRegistry(RESERVATION_ENTITY, RESERVATION_FIELD_KINDS, RESERVATION_ENUM_VALUES),
};

/** Look up the formatter spec for one (entityType, field), or undefined if unmapped. */
export function lookupFieldFormatter(entityType: string, field: string): FieldFormatterSpec | undefined {
  return ENTITY_FIELD_REGISTRY[entityType]?.[field];
}

/** Re-export the authoritative manifest so the drift test has one import site. */
export { ENTITY_CHANGE_FIELDS };
